import * as React from 'react';
// allowed because this file implements the wrapper
// eslint-disable-next-line no-restricted-imports
import Link, { LinkProps as OriginalLinkProps } from 'next/link';
import { RefAttributes } from 'react';

// Omitting passHref because it needs to be true, so that
// we can let the Orbit component handle the logic
type LinkProps = Omit<OriginalLinkProps, 'passHref'>;

type WrappedComponentProps<ComponentProps> = ComponentProps & LinkProps;

const LINK_PROPS = ['href', 'as', 'prefetch', 'replace', 'scroll', 'shallow', 'locale'];

function getComponentPropsObject<ComponentProps>(
  originalProps: WrappedComponentProps<ComponentProps>,
): ComponentProps {
  return Object.fromEntries(
    Object.entries(originalProps).filter(([key]) => !LINK_PROPS.includes(key)),
  ) as unknown as ComponentProps;
}

function getLinkPropsObject<ComponentProps>(
  originalProps: WrappedComponentProps<ComponentProps>,
): LinkProps {
  return {
    href: originalProps.href, // make typescript happy without casting
    ...Object.fromEntries(
      Object.entries(originalProps).filter(([key]) => LINK_PROPS.includes(key)),
    ),
  };
}

/**
 * Wraps a component with a Next.js <Link /> component.
 * This is useful to create versions of Orbit components that functions as
 * Next.js links.
 *
 * The resulting component can be used as the original component, but *also*
 * takes all the props of <Link /> from Next.js.
 *
 * @param Component - the component you want to use as a <Link />
 * @returns wrapped component that functions as a next <Link />
 */
export default function WrapWithLink<
  ComponentProps extends JSX.IntrinsicAttributes & RefAttributes<ComponentProps>,
>(Component) {
  const ComponentWrappedWithLink = (props: WrappedComponentProps<ComponentProps>) => {
    // Removing ref as tests complaining about passing a ref to FunctionalComponent (in this case TextLink)
    const ComponentWithoutForwardedRef = React.forwardRef<ComponentProps>(
      (propsWithChildren, _ref) => <Component {...propsWithChildren} />,
    );

    ComponentWithoutForwardedRef.displayName = 'ComponentWithoutForwardedRef';

    return (
      <Link {...getLinkPropsObject(props)} legacyBehavior passHref>
        <ComponentWithoutForwardedRef {...getComponentPropsObject<ComponentProps>(props)} />
      </Link>
    );
  };

  return ComponentWrappedWithLink;
}
